'use client'

import {type CSSProperties, useEffect, useMemo, useRef, useState} from 'react'
import styles from './styles.module.css'
import {cn} from '~/design-system/utils'

type MoneyRainProps = {
  active: boolean
  density?: number
  durationMs?: number
}

const EMOJI = '💰'

const MoneyRain = ({active, density = 100, durationMs = 5000}: MoneyRainProps) => {
  const [keySeed, setKeySeed] = useState(0)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!active) return
    setKeySeed(prev => prev + 1)
  }, [active])

  const drops = useMemo(() => {
    return Array.from({length: density}).map((_, idx) => {
      const left = Math.random() * 100
      // Negative delay ensures the animation starts mid-cycle for a seamless, constant rainfall
      const delay = Math.random() * durationMs
      const duration = durationMs - Math.random() * (durationMs / 4)
      const rotationDirection = Math.random() < 0.5 ? -1 : 1
      const rotateStart = `${Math.round(Math.random() * 360) - 180}deg`
      const rotateEnd = `${(Math.round(Math.random() * 720) - 360) * rotationDirection}deg`
      const transformOriginX = `${Math.round(Math.random() * 80) + 10}%`
      const transformOriginY = `${Math.round(Math.random() * 80) + 10}%`
      const opacity = Math.random() * 0.5 + 0.5
      return {
        id: `${keySeed}-${idx}`,
        left,
        delay,
        opacity,
        duration,
        rotateStart,
        rotateEnd,
        rotationDirection,
        transformOriginX,
        transformOriginY,
      }
    })
  }, [density, keySeed, durationMs])

  if (!active) return null

  return (
    <div
      ref={containerRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 z-10 h-screen w-screen overflow-hidden arcadia-display-8"
    >
      {drops.map(
        ({
          id,
          left,
          delay,
          opacity,
          rotateStart,
          rotateEnd,
          transformOriginX,
          transformOriginY,
          duration,
        }) => (
          <span
            key={id}
            className={cn(styles.drop, 'absolute select-none will-change-transform')}
            style={
              {
                '--rotate-start': rotateStart,
                '--rotate-end': rotateEnd,
                '--left': `${left}vw`,
                '--delay': `${delay}ms`,
                '--duration': `${duration}ms`,
                '--origin': `${transformOriginX} ${transformOriginY}`,
                '--opacity': opacity,
              } as CSSProperties
            }
          >
            {EMOJI}
          </span>
        )
      )}
    </div>
  )
}

export default MoneyRain
